/**
 * Configuración de idiomas de las landing pages SEO (Sub-fase 4).
 *
 * Arquitectura de URLs (acordada con el cliente):
 *  - Español: sin prefijo. `/tours/barcelona`, `/que-ver/roma`. No cambian.
 *  - Resto de idiomas: con prefijo de idioma + slug de ruta traducido.
 *    `/en/tours/barcelona`, `/fr/que-voir/roma`, `/de/sehenswuerdigkeiten/...`.
 *
 * El slug de ciudad NO se traduce (barcelona, roma, paris, londres…): solo
 * el nombre mostrado se localiza. Los slugs `tours` y `free-tour` son
 * términos casi universales en turismo y se mantienen; solo `que-ver` se
 * traduce a la keyword local de cada idioma.
 */
import { locales, defaultLocale, localeNames, type Locale } from '@/i18n/config';
import { LandingVariant } from './cityLandings';

export type LandingLocale = Locale;

/** Los 20 idiomas (es incluido). */
export const LANDING_LOCALES: readonly LandingLocale[] = locales;

/** Idioma por defecto: español, servido sin prefijo de URL. */
export const DEFAULT_LOCALE: LandingLocale = defaultLocale;

/** Los 19 idiomas que se sirven con prefijo de URL. */
export const TRANSLATED_LOCALES: LandingLocale[] = locales.filter(
  (l) => l !== defaultLocale,
);

/** Idiomas de escritura derecha-a-izquierda. */
export const RTL_LOCALES: LandingLocale[] = ['ar'];

export function isLandingLocale(value: string): value is LandingLocale {
  return (locales as readonly string[]).includes(value);
}

export function localeLabel(lang: LandingLocale): string {
  return localeNames[lang];
}

/**
 * Slug de la variante `que-ver` por idioma. Es la única que se traduce:
 * apunta a la keyword «qué ver / qué visitar» de cada mercado. Los idiomas
 * de alfabeto no latino usan el slug inglés (ASCII limpio, práctica común).
 */
const QUE_VER_SLUG: Record<LandingLocale, string> = {
  es: 'que-ver',
  ca: 'que-veure',
  gl: 'que-ver',
  eu: 'zer-ikusi',
  en: 'things-to-do',
  fr: 'que-voir',
  de: 'sehenswuerdigkeiten',
  it: 'cosa-vedere',
  pt: 'o-que-ver',
  nl: 'bezienswaardigheden',
  da: 'sevaerdigheder',
  sv: 'sevardheter',
  pl: 'co-zobaczyc',
  ru: 'dostoprimechatelnosti',
  uk: 'shcho-podyvytysya',
  ar: 'things-to-do',
  hi: 'things-to-do',
  ja: 'things-to-do',
  ko: 'things-to-do',
  zh: 'things-to-do',
};

/** Slug de ruta de una variante en un idioma dado. */
export function variantSlug(lang: LandingLocale, variant: LandingVariant): string {
  if (variant === 'tours') return 'tours';
  if (variant === 'free-tour') return 'free-tour';
  return QUE_VER_SLUG[lang] ?? QUE_VER_SLUG.en;
}

/** Resuelve la variante a partir del slug de ruta (`[tipo]`) y el idioma. */
export function variantFromSlug(
  lang: LandingLocale,
  slug: string,
): LandingVariant | null {
  if (slug === 'tours') return 'tours';
  if (slug === 'free-tour') return 'free-tour';
  if (slug === QUE_VER_SLUG[lang]) return 'que-ver';
  return null;
}

/** Ruta (path) de una landing: español sin prefijo, resto con prefijo. */
export function landingPath(
  lang: LandingLocale,
  variant: LandingVariant,
  citySlug: string,
): string {
  const tipo = variantSlug(lang, variant);
  return lang === DEFAULT_LOCALE
    ? `/${tipo}/${citySlug}`
    : `/${lang}/${tipo}/${citySlug}`;
}

/** Código hreflang BCP-47 de un idioma (el código de locale ya es válido). */
export function hreflangCode(lang: LandingLocale): string {
  return lang;
}

/**
 * Dada la ruta actual, devuelve la ruta equivalente en otro idioma.
 * Sirve tanto para páginas normales (`/blog` ↔ `/en/blog`) como para las
 * landing pages, donde además re-traduce el slug de la variante
 * (`/en/things-to-do/x` ↔ `/fr/que-voir/x`). Lo usa el selector de idioma.
 */
export function switchLocalePath(pathname: string, target: LandingLocale): string {
  const segs = pathname.split('/').filter(Boolean);

  // Quitar el prefijo de idioma actual, si lo hay.
  let current: LandingLocale = DEFAULT_LOCALE;
  if (segs[0] && isLandingLocale(segs[0]) && segs[0] !== DEFAULT_LOCALE) {
    current = segs[0] as LandingLocale;
    segs.shift();
  }

  // Landing page: `[tipo, ciudad]` con tipo de variante reconocible.
  if (segs.length === 2) {
    const variant = variantFromSlug(current, segs[0]);
    if (variant) return landingPath(target, variant, segs[1]);
  }

  // Página normal.
  const bare = '/' + segs.join('/');
  if (target === DEFAULT_LOCALE) return bare;
  return bare === '/' ? `/${target}` : `/${target}${bare}`;
}
